<script setup lang="ts">
import { ref, computed, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import { useTranslationStore } from '@/store/translation.store';
import axiosInstance from '@/services/interceptor.services';
import { useBodyScrollLock } from '@/composables/useBodyScrollLock';

const { t } = useI18n({ useScope: 'global' });

const translationStore = useTranslationStore();

// ── Mode d'affichage de la liste : 'card' | 'table' ───────────────────────────
const viewMode = ref<'card' | 'table'>('card');

// ── Drawer commerce (export/import) ──────────────────────────────────────────
const drawerOpen = ref(false);
const selectedStore = ref<any>(null);

useBodyScrollLock(toRef(drawerOpen, 'value'));

function openStore(s: any) {
  selectedStore.value = s;
  selectedItemTypes.value = [];
  selectedFile.value = null;
  translationStore.resetImport();
  drawerOpen.value = true;
}
function closeDrawer() {
  drawerOpen.value = false;
}

// ── Organisations (pour filtre) ─────────────────────────────────────────────────
const { data: orgsData } = useQuery({
  queryKey: ['trad-orgs'],
  queryFn: async () => {
    const r = await axiosInstance.get(
      'organizations/search?pagination=true&pageSize=100&sortBy=name&sortOrder=ASC',
    );
    return r.data;
  },
  staleTime: 5 * 60_000,
});
const organizations = computed<any[]>(() => {
  const d = orgsData.value as any;
  return d?.data ?? (Array.isArray(d) ? d : []);
});

const orgFilter = ref('');
const orgDropdownOpen = ref(false);
const orgSearch = ref('');
const filteredOrgs = computed(() =>
  organizations.value.filter((o) =>
    (o.name ?? o.designation ?? '')
      .toLowerCase()
      .includes(orgSearch.value.toLowerCase()),
  ),
);
function selectOrg(id: string) {
  orgFilter.value = id;
  orgDropdownOpen.value = false;
  orgSearch.value = '';
}
function clearOrg() {
  orgFilter.value = '';
  orgSearch.value = '';
  orgDropdownOpen.value = false;
}
const selectedOrgName = computed(() => {
  if (!orgFilter.value) return '';
  const o = organizations.value.find((o) => o.id === orgFilter.value);
  return o ? (o.name ?? o.designation ?? orgFilter.value) : orgFilter.value;
});

// ── Commerces (pagination serveur) ──────────────────────────────────────────────
const search = ref('');
const searchDebounced = ref('');
let debTimer: ReturnType<typeof setTimeout>;
function onSearch(v: string) {
  clearTimeout(debTimer);
  debTimer = setTimeout(() => {
    searchDebounced.value = v.trim();
    page.value = 1;
  }, 280);
}

const sortKey = ref<'designation' | 'created'>('designation');
const sortDir = ref<'asc' | 'desc'>('asc');

function toggleSort(key: typeof sortKey.value) {
  if (sortKey.value === key)
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
  page.value = 1;
}

const page = ref(1);
const pageSize = ref(10);
const PAGE_SIZES = [10, 20, 100] as const;

watch([orgFilter], () => {
  page.value = 1;
});

const { data: apiResp, isPending } = useQuery({
  queryKey: computed(() => [
    'trad-commerces',
    page.value,
    pageSize.value,
    searchDebounced.value,
    orgFilter.value,
    sortKey.value,
    sortDir.value,
  ]),
  queryFn: async () => {
    const params = new URLSearchParams();
    params.append('pagination', 'true');
    params.append('page', String(page.value));
    params.append('pageSize', String(pageSize.value));
    params.append(
      'sortBy',
      sortKey.value === 'designation' ? 'name' : 'created',
    );
    params.append('order', sortDir.value === 'asc' ? 'ASC' : 'DESC');
    if (searchDebounced.value)
      params.append('designation', searchDebounced.value);
    if (orgFilter.value) params.append('organizationId', orgFilter.value);
    const r = await axiosInstance.get(
      `restaurants/search?${params.toString()}`,
    );
    return r.data;
  },
});

const paginated = computed<any[]>(() => (apiResp.value as any)?.data ?? []);
const storeTotal = computed(() => (apiResp.value as any)?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(storeTotal.value / pageSize.value)),
);

function setPage(p: number) {
  page.value = Math.max(1, Math.min(p, totalPages.value));
}
function onPageSizeChange(e: Event) {
  pageSize.value = Number((e.target as HTMLSelectElement).value);
  page.value = 1;
}

// ── Helpers avatar ────────────────────────────────────────────────────────────
function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2)
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
const COLORS = [
  '#F68402',
  '#8B5CF6',
  '#06B6D4',
  '#EC4899',
  '#10B981',
  '#3B82F6',
  '#EF4444',
];
function avatarColor(id: string): string {
  return COLORS[id.charCodeAt(0) % COLORS.length];
}
function logoUrl(s: any): string | null {
  return s.logo?.info?.secureUrl ?? s.logoUrl ?? null;
}

// ── Export ────────────────────────────────────────────────────────────────────
const selectedItemTypes = ref<string[]>([]);

const itemTypeOptions = computed(() => [
  { value: 'public_products', label: t('translation.filters.public_products') },
  {
    value: 'public_categories',
    label: t('translation.filters.public_categories'),
  },
  {
    value: 'catalog_product_variants',
    label: t('translation.filters.catalog_product_variants'),
  },
]);

function handleExport() {
  translationStore.exportTranslations({
    storeId: selectedStore.value?.id,
    itemTypes: selectedItemTypes.value.length
      ? (selectedItemTypes.value as any)
      : undefined,
  });
}

// ── Import ────────────────────────────────────────────────────────────────────
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

function triggerFileInput() {
  fileInput.value?.click();
}
function handleFileSelect(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (f) selectedFile.value = f;
}
function handleDrop(e: DragEvent) {
  isDragging.value = false;
  const f = e.dataTransfer?.files?.[0];
  if (f && f.name.endsWith('.xlsx')) selectedFile.value = f;
}
async function handleImport() {
  if (!selectedFile.value) return;
  try {
    await translationStore.importTranslations(selectedFile.value);
  } catch {}
}
function clearFile() {
  selectedFile.value = null;
  translationStore.resetImport();
  if (fileInput.value) fileInput.value.value = '';
}
</script>

<template>
  <div class="tp-root">
    <div class="tp">
      <!-- ══════════════════════════════════════════════════════════════════════
       LISTE DES COMMERCES
  ═══════════════════════════════════════════════════════════════════════════ -->
      <div class="tp__header">
        <div>
          <p class="tp__eyebrow">{{ t('traductions.eyebrow') }}</p>
          <h1 class="tp__title">{{ t('traductions.title') }}</h1>
          <p class="tp__subtitle">{{ t('traductions.subtitle') }}</p>
        </div>
        <div class="tp__view-toggle">
          <button
            class="tp__view-btn"
            :class="{ 'tp__view-btn--active': viewMode === 'card' }"
            :title="t('traductions.viewCards')"
            @click="viewMode = 'card'"
          >
            <i class="pi pi-th-large" />
          </button>
          <button
            class="tp__view-btn"
            :class="{ 'tp__view-btn--active': viewMode === 'table' }"
            :title="t('traductions.viewTable')"
            @click="viewMode = 'table'"
          >
            <i class="pi pi-list" />
          </button>
        </div>
      </div>

      <div class="tp__kpis">
        <div class="tp__kpi">
          <span class="tp__kpi-val">{{ storeTotal }}</span>
          <span class="tp__kpi-label">{{ t('traductions.kpiStores') }}</span>
        </div>
        <div class="tp__kpi">
          <span class="tp__kpi-val tp__kpi-val--fr">FR</span>
          <span class="tp__kpi-label">{{
            t('traductions.kpiSourceLang')
          }}</span>
        </div>
        <div class="tp__kpi">
          <span class="tp__kpi-val">EN · NL</span>
          <span class="tp__kpi-label">{{
            t('traductions.kpiTargetLangs')
          }}</span>
        </div>
      </div>

      <div class="tp__card">
        <!-- Toolbar -->
        <div class="tp__toolbar" @click="orgDropdownOpen = false">
          <div class="tp__search-wrap">
            <i class="pi pi-search tp__search-ico" />
            <input
              :value="search"
              @input="
                (e) => {
                  search = (e.target as HTMLInputElement).value;
                  onSearch(search);
                }
              "
              type="text"
              :placeholder="t('traductions.filterPlaceholder')"
              class="tp__search"
            />
            <button
              v-if="search"
              class="tp__search-clear"
              @click="
                search = '';
                searchDebounced = '';
                page = 1;
              "
            >
              <i class="pi pi-times" />
            </button>
          </div>

          <div class="tp__dropdown-wrap" @click.stop>
            <button
              class="tp__dropdown-btn"
              :class="{ 'tp__dropdown-btn--active': orgFilter }"
              @click="orgDropdownOpen = !orgDropdownOpen"
            >
              <i class="pi pi-sitemap" />
              <span>{{ selectedOrgName || t('traductions.allOrgs') }}</span>
              <i v-if="orgFilter" class="pi pi-times" @click.stop="clearOrg" />
              <i v-else class="pi pi-chevron-down" />
            </button>
            <Transition name="tp-drop">
              <div v-if="orgDropdownOpen" class="tp__dropdown-panel">
                <div class="tp__dropdown-search-wrap">
                  <i class="pi pi-search" />
                  <input
                    v-model="orgSearch"
                    class="tp__dropdown-search"
                    :placeholder="t('traductions.searchOrg')"
                    @click.stop
                  />
                </div>
                <div class="tp__dropdown-list">
                  <button
                    class="tp__dropdown-item"
                    :class="{ 'tp__dropdown-item--active': !orgFilter }"
                    @click="clearOrg"
                  >
                    {{ t('traductions.allOrgs') }}
                  </button>
                  <button
                    v-for="o in filteredOrgs"
                    :key="o.id"
                    class="tp__dropdown-item"
                    :class="{ 'tp__dropdown-item--active': orgFilter === o.id }"
                    @click="selectOrg(o.id)"
                  >
                    {{ o.name ?? o.designation }}
                  </button>
                </div>
              </div>
            </Transition>
          </div>

          <span class="tp__count"
            >{{ storeTotal }}
            {{
              storeTotal !== 1
                ? t('traductions.stores')
                : t('traductions.store')
            }}</span
          >
        </div>

        <!-- En-têtes triables (mode tableau uniquement) -->
        <div v-if="viewMode === 'table'" class="tp__list-head">
          <button class="tp__sort-btn" @click="toggleSort('designation')">
            {{ t('traductions.colStore') }}
            <i
              class="pi tp__sort-ico"
              :class="
                sortKey === 'designation'
                  ? sortDir === 'asc'
                    ? 'pi-sort-amount-up'
                    : 'pi-sort-amount-down'
                  : 'pi-sort-alt'
              "
            />
          </button>
          <span class="tp__list-head-right">{{
            t('traductions.colLangs')
          }}</span>
        </div>

        <!-- Skeleton -->
        <div v-if="isPending" :class="viewMode === 'card' ? 'tp__cards' : ''">
          <div
            v-for="i in 6"
            :key="i"
            class="tp__row tp__row--sk"
            :class="{ 'tp__card-item tp__card-item--sk': viewMode === 'card' }"
          >
            <div class="tp__sk tp__sk--avatar" />
            <div
              class="tp__sk"
              style="height: 0.875rem; flex: 1; max-width: 200px"
            />
            <div style="display: flex; gap: 0.3rem">
              <div
                v-for="j in 3"
                :key="j"
                class="tp__sk"
                style="width: 1.75rem; height: 1.75rem; border-radius: 9999px"
              />
            </div>
          </div>
        </div>

        <!-- Liste : mode tableau -->
        <div v-else-if="viewMode === 'table'">
          <div v-if="!paginated.length" class="tp__empty">
            <i class="pi pi-language" />
            <span>{{ t('traductions.noStore') }}</span>
          </div>
          <div
            v-for="s in paginated"
            :key="s.id"
            class="tp__row"
            @click="openStore(s)"
          >
            <div
              class="tp__avatar"
              :style="logoUrl(s) ? '' : `background:${avatarColor(s.id)}`"
            >
              <img
                v-if="logoUrl(s)"
                :src="logoUrl(s)"
                class="tp__avatar-img"
                alt=""
              />
              <span v-else>{{ initials(s.designation ?? '?') }}</span>
            </div>
            <span class="tp__store-name">{{ s.designation }}</span>
            <div class="tp__langs">
              <span class="tp__lang tp__lang--fr">FR</span>
              <span class="tp__lang">EN</span>
              <span class="tp__lang">NL</span>
            </div>
            <i class="pi pi-chevron-right tp__row-arrow" />
          </div>
        </div>

        <!-- Liste : mode carte -->
        <div v-else>
          <div v-if="!paginated.length" class="tp__empty">
            <i class="pi pi-language" />
            <span>{{ t('traductions.noStore') }}</span>
          </div>
          <div v-else class="tp__cards">
            <button
              v-for="s in paginated"
              :key="s.id"
              class="tp__card-item"
              @click="openStore(s)"
            >
              <div
                class="tp__avatar"
                :style="logoUrl(s) ? '' : `background:${avatarColor(s.id)}`"
              >
                <img
                  v-if="logoUrl(s)"
                  :src="logoUrl(s)"
                  class="tp__avatar-img"
                  alt=""
                  loading="lazy"
                />
                <span v-else>{{ initials(s.designation ?? '?') }}</span>
              </div>
              <span class="tp__card-item-name">{{ s.designation }}</span>
              <div class="tp__langs">
                <span class="tp__lang tp__lang--fr">FR</span>
                <span class="tp__lang">EN</span>
                <span class="tp__lang">NL</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="!isPending && storeTotal > 0" class="tp__pagination">
          <div class="tp__pg-left">
            <span class="tp__pg-info">
              {{ (page - 1) * pageSize + 1 }}–{{
                Math.min(page * pageSize, storeTotal)
              }}
              {{ t('traductions.paginationOf') }} {{ storeTotal }}
            </span>
            <select
              class="tp__pg-size"
              :value="pageSize"
              @change="onPageSizeChange"
            >
              <option v-for="sz in PAGE_SIZES" :key="sz" :value="sz">
                {{ sz }} {{ t('traductions.perPage') }}
              </option>
            </select>
          </div>
          <div class="tp__pg-btns">
            <button
              class="tp__pg-btn"
              @click="setPage(1)"
              :disabled="page === 1"
            >
              <i class="pi pi-angle-double-left" />
            </button>
            <button
              class="tp__pg-btn"
              @click="setPage(page - 1)"
              :disabled="page === 1"
            >
              <i class="pi pi-angle-left" />
            </button>
            <span class="tp__pg-current">{{ page }} / {{ totalPages }}</span>
            <button
              class="tp__pg-btn"
              @click="setPage(page + 1)"
              :disabled="page === totalPages"
            >
              <i class="pi pi-angle-right" />
            </button>
            <button
              class="tp__pg-btn"
              @click="setPage(totalPages)"
              :disabled="page === totalPages"
            >
              <i class="pi pi-angle-double-right" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════
     DRAWER : ESPACE DE TRADUCTION D'UN COMMERCE
═══════════════════════════════════════════════════════════════════════════ -->
    <Teleport to="body">
      <Transition name="tp-backdrop">
        <div v-if="drawerOpen" class="tp-backdrop" @click="closeDrawer" />
      </Transition>
      <Transition name="tp-drawer">
        <aside v-if="drawerOpen && selectedStore" class="tp-drawer" @click.stop>
          <div class="tp-drawer__inner">
            <!-- Header -->
            <div class="tp__breadcrumb">
              <button class="tp__back" @click="closeDrawer">
                <i class="pi pi-times" />
                <span>{{ t('traductions.title') }}</span>
              </button>
              <span class="tp__bc-sep">/</span>
              <div class="tp__bc-store">
                <div
                  class="tp__avatar tp__avatar--sm"
                  :style="
                    logoUrl(selectedStore)
                      ? ''
                      : `background:${avatarColor(selectedStore.id)}`
                  "
                >
                  <img
                    v-if="logoUrl(selectedStore)"
                    :src="logoUrl(selectedStore)"
                    class="tp__avatar-img"
                    alt=""
                  />
                  <span v-else>{{
                    initials(selectedStore.designation ?? '?')
                  }}</span>
                </div>
                <span class="tp__bc-name">{{ selectedStore.designation }}</span>
              </div>
            </div>

            <!-- Titre -->
            <div class="tp__header">
              <div>
                <p class="tp__eyebrow">
                  {{ t('traductions.workspaceEyebrow') }}
                </p>
                <h1 class="tp__title">{{ selectedStore.designation }}</h1>
                <p class="tp__subtitle">
                  {{ t('traductions.workspaceSubtitle') }}
                </p>
              </div>
            </div>

            <!-- Contenu : export + import -->
            <div class="tp__store-grid">
              <!-- Export -->
              <div class="tp__panel tp__panel--card">
                <div class="tp__panel-heading">
                  <i class="pi pi-download tp__panel-ico" />
                  <span>{{ t('translation.export_btn') }}</span>
                </div>
                <p class="tp__panel-desc">
                  {{ t('translation.export_desc') }}
                </p>

                <div class="tp__filter-group">
                  <p class="tp__filter-label">
                    {{ t('translation.filters.itemTypes_label') }}
                  </p>
                  <div class="tp__checkboxes">
                    <label
                      v-for="opt in itemTypeOptions"
                      :key="opt.value"
                      class="tp__checkbox-label"
                    >
                      <input
                        type="checkbox"
                        :value="opt.value"
                        v-model="selectedItemTypes"
                        class="tp__checkbox"
                      />
                      {{ opt.label }}
                    </label>
                  </div>
                  <p v-if="!selectedItemTypes.length" class="tp__filter-hint">
                    {{ t('translation.filters.all_hint') }}
                  </p>
                </div>

                <button
                  class="tp__btn tp__btn--primary"
                  @click="handleExport"
                  :disabled="translationStore.isExporting"
                >
                  <span
                    v-if="translationStore.isExporting"
                    class="tp__spinner"
                  />
                  <i v-else class="pi pi-download" />
                  {{
                    translationStore.isExporting
                      ? t('translation.exporting')
                      : t('translation.export_btn')
                  }}
                </button>
              </div>

              <!-- Import -->
              <div class="tp__panel tp__panel--card">
                <div class="tp__panel-heading">
                  <i class="pi pi-upload tp__panel-ico" />
                  <span>{{ t('translation.validate') }}</span>
                </div>
                <p class="tp__panel-desc">{{ t('translation.import_desc') }}</p>

                <div
                  class="tp__dropzone"
                  :class="{
                    'tp__dropzone--drag': isDragging,
                    'tp__dropzone--ok': selectedFile,
                  }"
                  @click="triggerFileInput"
                  @drop.prevent="handleDrop"
                  @dragover.prevent="isDragging = true"
                  @dragleave="isDragging = false"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx"
                    style="display: none"
                    @change="handleFileSelect"
                  />
                  <div v-if="selectedFile" class="tp__dropzone-inner">
                    <i
                      class="pi pi-file-excel tp__dropzone-ico tp__dropzone-ico--ok"
                    />
                    <span class="tp__dropzone-name">{{
                      selectedFile.name
                    }}</span>
                    <span class="tp__dropzone-size"
                      >{{ (selectedFile.size / 1024).toFixed(1) }} KB</span
                    >
                  </div>
                  <div v-else class="tp__dropzone-inner">
                    <i class="pi pi-cloud-upload tp__dropzone-ico" />
                    <span class="tp__dropzone-hint">{{
                      t('translation.drop_zone')
                    }}</span>
                  </div>
                </div>

                <!-- Progress -->
                <div
                  v-if="translationStore.isImporting"
                  class="tp__progress-wrap"
                >
                  <div class="tp__progress-head">
                    <span>{{ t('translation.importing') }}</span>
                    <span>{{ translationStore.uploadProgress }}%</span>
                  </div>
                  <div class="tp__progress-track">
                    <div
                      class="tp__progress-bar"
                      :style="{ width: translationStore.uploadProgress + '%' }"
                    />
                  </div>
                </div>

                <!-- Rapport -->
                <div
                  v-if="translationStore.importReport"
                  class="tp__report"
                  :class="
                    translationStore.importReport.errorCount > 0
                      ? 'tp__report--warn'
                      : 'tp__report--ok'
                  "
                >
                  <p class="tp__report-line">
                    <i
                      class="pi"
                      :class="
                        translationStore.importReport.errorCount > 0
                          ? 'pi-exclamation-triangle'
                          : 'pi-check-circle'
                      "
                    />
                    {{
                      t('translation.import_success', {
                        count: translationStore.importReport.successCount,
                      })
                    }}
                  </p>
                  <p
                    v-if="translationStore.importReport.errorCount > 0"
                    class="tp__report-sub"
                  >
                    {{
                      t('translation.import_errors', {
                        count: translationStore.importReport.errorCount,
                      })
                    }}
                  </p>
                  <ul
                    v-if="translationStore.importReport.errors?.length"
                    class="tp__report-errors"
                  >
                    <li
                      v-for="(err, i) in translationStore.importReport.errors"
                      :key="i"
                    >
                      <code>{{ err.rowId }}</code> {{ err.message }}
                    </li>
                  </ul>
                </div>

                <!-- Actions -->
                <div class="tp__actions">
                  <button
                    class="tp__btn tp__btn--primary"
                    @click="handleImport"
                    :disabled="!selectedFile || translationStore.isImporting"
                  >
                    <span
                      v-if="translationStore.isImporting"
                      class="tp__spinner"
                    />
                    <i v-else class="pi pi-check" />
                    {{ t('translation.validate') }}
                  </button>
                  <button
                    class="tp__btn tp__btn--ghost"
                    @click="clearFile"
                    :disabled="translationStore.isImporting"
                  >
                    {{ t('translation.clear') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.tp-root {
  display: contents;
}
.tp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem 2.25rem;
}

.tp__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

/* Toggle vue carte/tableau */
.tp__view-toggle {
  display: flex;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  overflow: hidden;
  flex-shrink: 0;
}
.tp__view-btn {
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  cursor: pointer;
}
.tp__view-btn--active {
  background: #0b1320;
  color: #fff;
}
[data-theme='dark'] .tp__view-btn--active {
  background: #ffd60a;
  color: #0b1320;
}

/* Cards (mode carte) */
.tp__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.25rem;
  padding: 1.25rem;
}
.tp__card-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  font-family: inherit;
  text-align: center;
  transition:
    box-shadow 0.3s cubic-bezier(0.2, 1, 0.3, 1),
    transform 0.3s cubic-bezier(0.2, 1, 0.3, 1);
}
.tp__card-item:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.tp__card-item--sk {
  cursor: default;
}
.tp__card-item--sk:hover {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  transform: none;
}
.tp__card-item-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Drawer traduction commerce */
.tp-backdrop {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(8, 12, 20, 0.45);
  backdrop-filter: blur(2px);
}
.tp-backdrop-enter-active,
.tp-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.tp-backdrop-enter-from,
.tp-backdrop-leave-to {
  opacity: 0;
}
.tp-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 901;
  width: min(560px, 100vw);
  background: #f4f6f9;
  box-shadow: -4px 0 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}
[data-theme='dark'] .tp-drawer {
  background: #0c1421;
}
.tp-drawer-enter-active,
.tp-drawer-leave-active {
  transition: transform 0.25s ease;
}
.tp-drawer-enter-from,
.tp-drawer-leave-to {
  transform: translateX(100%);
}
.tp-drawer__inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.75rem;
  box-sizing: border-box;
  overflow-y: auto;
}

/* Header */
.tp__eyebrow {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-primary);
  margin: 0 0 0.35rem;
  opacity: 0.8;
}
.tp__title {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  margin: 0 0 0.3rem;
  letter-spacing: -0.02em;
  line-height: 1.2;
}
.tp__subtitle {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  margin: 0;
}

/* Breadcrumb */
.tp__breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.tp__back {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-primary);
  padding: 0;
}
.tp__back:hover {
  opacity: 0.7;
}
.tp__bc-sep {
  color: var(--nxt-on-surface-variant);
  opacity: 0.35;
  font-size: 0.875rem;
}
.tp__bc-store {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.tp__bc-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}

/* KPIs */
.tp__kpis {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}
.tp__kpi {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  border-radius: 0.875rem;
  padding: 0.875rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 8rem;
}
.tp__kpi-val {
  font-size: 1.625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  letter-spacing: -0.02em;
  line-height: 1;
}
.tp__kpi-val--fr {
  color: var(--nxt-primary);
}
.tp__kpi-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  opacity: 0.6;
}

/* Card liste */
.tp__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

/* Toolbar */
.tp__toolbar {
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
}

.tp__dropdown-wrap {
  position: relative;
}
.tp__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.tp__dropdown-btn--active {
  border-color: #ffd60a;
  color: #0b1320;
}
.tp__dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 200;
  min-width: 16rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.tp__dropdown-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.tp__dropdown-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.tp__dropdown-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.375rem;
}
.tp__dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.tp__dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.tp__dropdown-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: #0b1320;
}
.tp-drop-enter-active,
.tp-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.tp-drop-enter-from,
.tp-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.tp__search-wrap {
  position: relative;
  flex: 1;
  max-width: 340px;
}
.tp__search-ico {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: var(--nxt-on-surface-variant);
  pointer-events: none;
}
.tp__search {
  width: 100%;
  padding: 0.55rem 0.75rem 0.55rem 2.1rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  background: var(--nxt-surface-low, #f8f8fa);
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.tp__search:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg, #fff);
}
.tp__search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  border: none;
  background: var(--nxt-outline-variant, #ddd);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  color: var(--nxt-on-surface-variant);
}
.tp__search-clear:hover {
  background: var(--nxt-primary);
  color: #fff;
}
.tp__count {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  margin-left: auto;
}

/* En-têtes triables */
.tp__list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
}
.tp__sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
  opacity: 0.55;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: opacity 0.14s;
}
.tp__sort-btn:hover {
  opacity: 0.9;
}
.tp__sort-ico {
  font-size: 0.65rem;
}
.tp__list-head-right {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
  opacity: 0.55;
}

/* Pagination */
.tp__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
}
.tp__pg-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.tp__pg-info {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.tp__pg-size {
  font-size: 0.75rem;
  font-family: inherit;
  padding: 0.25rem 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.375rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  cursor: pointer;
  outline: none;
}
.tp__pg-btns {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.tp__pg-btn {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.375rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: var(--nxt-card-bg, #fff);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  color: var(--nxt-on-surface-variant);
  transition:
    background 0.12s,
    border-color 0.12s;
}
.tp__pg-btn:hover:not(:disabled) {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.tp__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.tp__pg-current {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  padding: 0 0.25rem;
  white-space: nowrap;
}

/* Lignes */
.tp__row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.05));
  transition: background 0.12s;
}
.tp__row:last-child {
  border-bottom: none;
}
.tp__row:hover {
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.025));
}
.tp__row--sk {
  cursor: default;
}
.tp__row--sk:hover {
  background: none;
}

/* Avatar */
.tp__avatar {
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  overflow: hidden;
}
.tp__avatar--sm {
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  font-size: 0.625rem;
  border-radius: 0.375rem;
}
.tp__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tp__store-name {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tp__langs {
  display: flex;
  gap: 0.3rem;
}
.tp__lang {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5625rem;
  font-weight: 700;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
}
.tp__lang--fr {
  border-color: var(--nxt-primary);
  background: rgba(var(--nxt-primary-rgb, 246, 132, 2), 0.08);
  color: var(--nxt-primary);
}
.tp__row-arrow {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.3;
}

/* Vide */
.tp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-variant);
}
.tp__empty .pi {
  font-size: 1.75rem;
  opacity: 0.3;
}

/* Grid store — colonne unique (contexte drawer étroit) */
.tp__store-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;
}

.tp__panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.tp__panel--card {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  border-radius: 0.875rem;
  padding: 1.5rem;
}
.tp__panel-heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.tp__panel-ico {
  font-size: 1rem;
  opacity: 0.6;
}
.tp__panel-desc {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  margin: 0;
}

/* Filtres */
.tp__filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.tp__filter-label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-variant);
  opacity: 0.55;
}
.tp__checkboxes {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.tp__checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  cursor: pointer;
  user-select: none;
}
.tp__checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: var(--nxt-primary);
  cursor: pointer;
}
.tp__filter-hint {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.5;
  margin: 0;
}

/* Boutons */
.tp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.14s,
    opacity 0.14s;
  border: 1.5px solid transparent;
  white-space: nowrap;
}
.tp__btn--primary {
  background: var(--nxt-primary);
  color: #fff;
  border-color: var(--nxt-primary);
}
.tp__btn--primary:hover {
  opacity: 0.88;
}
.tp__btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.tp__btn--ghost {
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  border-color: var(--nxt-outline-variant, #e0e0e0);
}
.tp__btn--ghost:hover {
  background: var(--nxt-surface-low, #f0f0f2);
}
.tp__btn--ghost:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.tp__actions {
  display: flex;
  gap: 0.5rem;
}

/* Spinner */
.tp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  animation: tp-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes tp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Dropzone */
.tp__dropzone {
  border: 2px dashed var(--nxt-outline-variant, #d0d0d0);
  border-radius: 0.75rem;
  padding: 2rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    border-color 0.14s,
    background 0.14s;
  background: var(--nxt-surface-low, #f8f8fa);
}
.tp__dropzone:hover {
  border-color: var(--nxt-primary);
}
.tp__dropzone--drag {
  border-color: var(--nxt-primary);
  background: rgba(var(--nxt-primary-rgb, 246, 132, 2), 0.03);
}
.tp__dropzone--ok {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.04);
}
.tp__dropzone-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  text-align: center;
}
.tp__dropzone-ico {
  font-size: 2rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.35;
}
.tp__dropzone-ico--ok {
  color: #10b981;
  opacity: 1;
}
.tp__dropzone-hint {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
.tp__dropzone-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #059669;
}
.tp__dropzone-size {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.6;
}

/* Progress */
.tp__progress-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.tp__progress-head {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.tp__progress-track {
  height: 0.3rem;
  background: var(--nxt-outline-variant, #e0e0e0);
  border-radius: 9999px;
  overflow: hidden;
}
.tp__progress-bar {
  height: 100%;
  background: var(--nxt-primary);
  border-radius: 9999px;
  transition: width 0.3s;
}

/* Rapport */
.tp__report {
  border-radius: 0.625rem;
  padding: 0.875rem 1rem;
  border: 1.5px solid;
}
.tp__report--ok {
  border-color: rgba(16, 185, 129, 0.3);
  background: rgba(16, 185, 129, 0.06);
}
.tp__report--warn {
  border-color: rgba(245, 158, 11, 0.3);
  background: rgba(245, 158, 11, 0.06);
}
.tp__report-line {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0 0 0.2rem;
}
.tp__report--ok .pi {
  color: #059669;
}
.tp__report--warn .pi {
  color: #d97706;
}
.tp__report-sub {
  font-size: 0.75rem;
  color: #d97706;
  margin: 0 0 0.4rem;
}
.tp__report-errors {
  list-style: none;
  padding: 0;
  margin: 0.4rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 8rem;
  overflow-y: auto;
}
.tp__report-errors li {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  background: var(--nxt-card-bg, #fff);
  border-radius: 0.375rem;
  padding: 0.3rem 0.6rem;
}
.tp__report-errors code {
  font-family: monospace;
  opacity: 0.5;
  margin-right: 0.4rem;
}

/* Skeleton */
@keyframes tp-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.tp__sk {
  border-radius: 0.375rem;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06) 25%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.06) 75%
  );
  background-size: 200% 100%;
  animation: tp-shimmer 1.4s infinite;
}
.tp__sk--avatar {
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
}
[data-theme='dark'] .tp__sk {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.09) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
}
</style>

<style>
/* ── Dark mode overrides ─────────────────────────────────────────────── */
[data-theme='dark'] .tp {
  background: #0c1421;
}
[data-theme='dark'] .tp__title,
[data-theme='dark'] .tp__bc-name {
  color: #f1f5f9;
}
[data-theme='dark'] .tp__subtitle,
[data-theme='dark'] .tp__panel-desc {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .tp__eyebrow,
[data-theme='dark'] .tp__back,
[data-theme='dark'] .tp__kpi-val--fr {
  color: #ffd60a;
}

/* KPIs */
[data-theme='dark'] .tp__kpi {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .tp__kpi-val {
  color: #f1f5f9;
}
[data-theme='dark'] .tp__kpi-label {
  color: rgba(255, 255, 255, 0.5);
}

/* Card liste */
[data-theme='dark'] .tp__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .tp__toolbar,
[data-theme='dark'] .tp__list-head,
[data-theme='dark'] .tp__pagination {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .tp__count,
[data-theme='dark'] .tp__pg-info,
[data-theme='dark'] .tp__sort-btn,
[data-theme='dark'] .tp__list-head-right {
  color: rgba(255, 255, 255, 0.5);
}

/* Recherche */
[data-theme='dark'] .tp__search {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .tp__search:focus {
  border-color: #ffd60a;
  background: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .tp__search-ico {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .tp__search-clear {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .tp__search-clear:hover {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .tp__dropdown-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .tp__dropdown-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .tp__dropdown-item:hover,
[data-theme='dark'] .tp__dropdown-item--active {
  background: rgba(255, 255, 255, 0.08);
}

/* Pagination */
[data-theme='dark'] .tp__pg-size,
[data-theme='dark'] .tp__pg-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .tp__pg-btn:hover:not(:disabled) {
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .tp__pg-current {
  color: #f1f5f9;
}

/* Lignes */
[data-theme='dark'] .tp__row {
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .tp__row:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* Cards (mode carte) */
[data-theme='dark'] .tp__card-item {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
[data-theme='dark'] .tp__card-item:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}
[data-theme='dark'] .tp__card-item-name {
  color: #f1f5f9;
}
[data-theme='dark'] .tp__store-name {
  color: #f1f5f9;
}
[data-theme='dark'] .tp__empty {
  color: rgba(255, 255, 255, 0.5);
}

/* Langues */
[data-theme='dark'] .tp__lang {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .tp__lang--fr {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.12);
  color: #ffd60a;
}

/* Panneaux export/import */
[data-theme='dark'] .tp__panel--card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .tp__panel-heading {
  color: #f1f5f9;
}
[data-theme='dark'] .tp__filter-label {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .tp__checkbox-label {
  color: rgba(255, 255, 255, 0.85);
}
[data-theme='dark'] .tp__filter-hint {
  color: rgba(255, 255, 255, 0.4);
}

/* Boutons */
[data-theme='dark'] .tp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
[data-theme='dark'] .tp__btn--primary .tp__spinner {
  border-color: rgba(11, 19, 32, 0.3);
  border-top-color: #0b1320;
}
[data-theme='dark'] .tp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .tp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* Dropzone */
[data-theme='dark'] .tp__dropzone {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .tp__dropzone:hover {
  border-color: #ffd60a;
}
[data-theme='dark'] .tp__dropzone--drag {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.06);
}
[data-theme='dark'] .tp__dropzone--ok {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.08);
}
[data-theme='dark'] .tp__dropzone-ico {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .tp__dropzone-hint {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .tp__dropzone-name {
  color: #34d399;
}
[data-theme='dark'] .tp__dropzone-size {
  color: rgba(255, 255, 255, 0.5);
}

/* Progress */
[data-theme='dark'] .tp__progress-head {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .tp__progress-track {
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .tp__progress-bar {
  background: #ffd60a;
}

/* Rapport */
[data-theme='dark'] .tp__report--ok {
  border-color: rgba(16, 185, 129, 0.4);
  background: rgba(16, 185, 129, 0.1);
}
[data-theme='dark'] .tp__report--warn {
  border-color: rgba(245, 158, 11, 0.4);
  background: rgba(245, 158, 11, 0.1);
}
[data-theme='dark'] .tp__report-line {
  color: #f1f5f9;
}
[data-theme='dark'] .tp__report--ok .pi {
  color: #34d399;
}
[data-theme='dark'] .tp__report--warn .pi {
  color: #fbbf24;
}
[data-theme='dark'] .tp__report-sub {
  color: #fbbf24;
}
[data-theme='dark'] .tp__report-errors li {
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.05);
}
</style>
